import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { SvgIcons } from './svg-icons';

interface IngredientIconPickerProps {
  /** Currently selected icon ID */
  value: string;
  /** Callback when icon is selected */
  onSelect: (iconId: string) => void;
  /** Label for the picker */
  label?: string;
}

/**
 * Icon categories for organized display
 */
const ICON_CATEGORIES = {
  vegetables: {
    label: { fr: 'Légumes', ar: 'خضروات' },
    icons: [
      'tomato',
      'carrot',
      'onion',
      'red-onion',
      'green-pepper',
      'red-pepper',
      'yellow-pepper',
      'turnip',
      'cucumber',
      'eggplant',
      'salad',
      'broccoli',
      'cabbage',
      'potato',
      'mushroom',
      'zuccini',
      'pumpkin',
      'cauliflower',
      'betravet',
      'tomate-cerise',
    ],
  },
  fruits: {
    label: { fr: 'Fruits', ar: 'فواكه' },
    icons: [
      'apple',
      'orange',
      'citrus',
      'bananas',
      'avocado',
      'strawberry',
      'grapes',
      'pineapple',
      'pear',
      'mango',
      'peach',
      'abricot',
      'watermelon',
      'kiwi',
      'cherries',
      'coconut',
      'berries',
      'citron',
    ],
  },
  meat: {
    label: { fr: 'Viandes & Protéines', ar: 'لحوم وبروتينات' },
    icons: [
      'chicken',
      'sheep',
      'cow',
      'meat-minced',
      'meat-steak',
      'charcuterie',
      'sausage',
      'nugget',
      'chicken-leg',
      'turkey',
      'fish',
      'salami',
      'brochette1',
      'brochette2',
      'brochette3',
      'burger-patty',
    ],
  },
  seafood: {
    label: { fr: 'Fruits de mer', ar: 'مأكولات بحرية' },
    icons: ['shrimp', 'calamar', 'seafood', 'salmon', 'daurade', 'sardine', 'fish1', 'fish2'],
  },
  dairy: {
    label: { fr: "Produits d'origine animale", ar: 'المنتجات الحيوانية' },
    icons: [
      'milk',
      'cheese',
      'cheese1',
      'yogurt',
      'jben',
      'mozzarella',
      'cheese-slice',
      'butter',
      'eggs',
      'honey',
      'cream',
      'mascarpone',
      'whipped-cream',
      'bleuCheese',
    ],
  },
  grains: {
    label: { fr: 'Céréales & Pâtes', ar: 'حبوب ومعكرونة' },
    icons: [
      'flour',
      'rice',
      'pasta',
      'spaghetti',
      'macaroni',
      'tagliatelle',
      'lasagne',
      'penne',
      'lentil',
      'red-beans',
      'peas',
      'corn',
      'couscous',
      'poisChiche',
    ],
  },
  paintry: {
    label: { fr: 'Boulangerie', ar: 'مخبوزات' },
    icons: [
      'croissant',
      'buns',
      'bread',
      'burger-bun',
      'tortilla',
      'panini',
      'toast',
      'cookies',
      'donut',
      'pain-au-chocolat',
      'baguette',
      'crepe',
      'pancake',
      'warqa',
      'msemen',
    ],
  },
  condiments: {
    label: { fr: 'Huiles & Condiments', ar: 'زيوت وتوابل' },
    icons: ['vegetable-oil', 'olive-oil', 'garlic', 'french-fries', 'black-olive', 'green-olive', 'raisinsSec','pruneSec','dattes','noix'],
  },
  beverages: {
    label: { fr: 'Boissons', ar: 'مشروبات' },
    icons: [
      'water',
      'milk',
      'coffee',
      'tea',
      'orange-juice',
      'soda',
      'soda2',
      'soda3',
      'smoothie',
      'juice1',
    ],
  },
  others: {
    label: { fr: 'Autres', ar: 'أخرى' },
    icons: [
      'nutella',
      'pistachio',
      'chocolate',
      'chocolate2',
      'caramel',
      'tuna',
      'sugar',
      'can',
      'can2',
      'fruits',
      'vegetables',
      'jar1',
      'sauce1',
      'sauce2',
      'sauce3',
      'sauce4',
      'sauce5',
      'sauce6',
      'item1',
      'item2',
      'item3',
      'item4',
      'item5',
      'item6',
      'item7',
      'teaBag',
    ],
  },
};

/**
 * IngredientIconPicker component
 */
export function IngredientIconPicker({ value, onSelect, label }: IngredientIconPickerProps) {
  const [open, setOpen] = React.useState(false);
  const SelectedIcon = SvgIcons[value as keyof typeof SvgIcons] || SvgIcons['tomato'];

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => setOpen(!open)}
          className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-24 lg:h-24 p-2 flex-shrink-0 flex items-center justify-center"
        >
          {SelectedIcon && (
            <SelectedIcon className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-20 lg:h-20" />
          )}
        </Button>
      </div>

      {open && (
        <div className="p-2 sm:p-4 border rounded-md bg-background max-h-[60vh] sm:max-h-[400px] overflow-y-auto">
          {Object.entries(ICON_CATEGORIES).map(([key, category]) => (
            <div key={key} className="mb-4">
              <p className="text-xs sm:text-sm font-medium mb-2">{category.label.fr}</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 lg:gap-1  md:grid-cols-6">
                {category.icons.map((iconId) => {
                  const Icon = SvgIcons[iconId] || SvgIcons['dish'];
                  return (
                    <button
                      key={iconId}
                      type="button"
                      onClick={() => {
                        onSelect(iconId);
                        setOpen(false);
                      }}
                      className={cn(
                        'hover:bg-muted rounded transition-colors flex items-center justify-center border-2 aspect-square',
                        value === iconId ? 'border-primary bg-primary/10' : 'border-transparent'
                      )}
                      title={iconId}
                    >
                      {Icon && (
                        <Icon className="w-12 h-12 sm:w-20 sm:h-20 md:w-20 md:h-20 lg:w-40 lg:h-40" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
